import { useState } from 'react';

import {
  Button,
  Icons,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@commenda/kakashi';
import { cn } from '@commenda/utils';

const MONTH_NAMES_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

export interface IndirectTaxMonthYearValue {
  month: number; // 1-12
  year: number;
}

interface IndirectTaxMonthYearPickerProps {
  value: IndirectTaxMonthYearValue;
  onChange: (value: IndirectTaxMonthYearValue) => void;
}

const IndirectTaxMonthYearPicker = ({
  value,
  onChange,
}: IndirectTaxMonthYearPickerProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [viewYear, setViewYear] = useState(value.year);

  const handleMonthSelect = (month: number) => {
    onChange({ month, year: viewYear });
    setIsOpen(false);
  };

  const displayLabel = `${MONTH_NAMES_SHORT[value.month - 1]} ${value.year}`;

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'flex items-center gap-xl rounded-sm border px-md py-sm',
            'bg-bg-primary txt-small-medium text-text-primary transition-colors',
            'hover:border-border-primary-hover'
          )}
        >
          <div className="flex items-center gap-md">
            <Icons.Calendar className="size-4 text-text-tertiary" />
            <span className="txt-small-regular text-text-tertiary">
              {displayLabel}
            </span>
          </div>

          <Icons.ChevronDown className="size-4 text-text-tertiary" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-lg" align="start">
        {/* Year navigation */}
        <div className="flex items-center justify-between mb-lg">
          <Button
            variant="tertiary"
            size="sm"
            onClick={() => setViewYear((y) => y - 1)}
            type="button"
          >
            <Icons.ChevronLeft className="size-4" />
          </Button>
          <span className="txt-small-bold text-text-primary">{viewYear}</span>
          <Button
            variant="tertiary"
            size="sm"
            onClick={() => setViewYear((y) => y + 1)}
            type="button"
          >
            <Icons.ChevronRight className="size-4" />
          </Button>
        </div>

        {/* Month grid */}
        <div className="grid grid-cols-3 gap-md">
          {MONTH_NAMES_SHORT.map((monthName, index) => {
            const monthNum = index + 1;
            const isSelected =
              value.month === monthNum && value.year === viewYear;

            return (
              <Button
                key={monthName}
                variant={isSelected ? 'primary' : 'tertiary'}
                size="sm"
                className="w-full"
                onClick={() => handleMonthSelect(monthNum)}
                type="button"
              >
                {monthName}
              </Button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export { IndirectTaxMonthYearPicker };
